export interface Problem {
  slug: string;
  icon: string; // lucide
  titleDe: string;
  titleEn: string;
  shortDe: string;
  shortEn: string;
  // Long-tail SEO keywords
  keywordsDe: string[];
  // Hero / intro
  introDe: string;
  introEn: string;
  // Sofortmaßnahmen — what to do right now
  stepsDe: string[];
  stepsEn: string[];
  // Common causes
  causesDe: string[];
  causesEn: string[];
  // Related service slug (from services.ts) for cross-link
  relatedServiceSlug: string;
  faqs: { q: { de: string; en: string }; a: { de: string; en: string } }[];
  priceFrom: number;
}

export const problems: Problem[] = [
  {
    slug: "schluessel-verloren-wien",
    icon: "KeyRound",
    titleDe: "Schlüssel verloren in Wien – was tun?",
    titleEn: "Lost key in Vienna – what to do?",
    shortDe: "Schlüssel verloren? Wir öffnen, tauschen den Zylinder und sichern Ihre Wohnung – 24h in ganz Wien.",
    shortEn: "Lost your key? We open, replace the cylinder and secure your home – 24/7 across Vienna.",
    keywordsDe: ["Schlüssel verloren Wien", "Schlüssel weg Wien", "Schlüsseldienst nach Schlüsselverlust"],
    introDe: "Schlüssel verloren ist Stress – aber kein Drama. Wir öffnen Ihre Tür schadenfrei, tauschen bei Bedarf den Zylinder gegen einen sicheren Marken-Zylinder und beraten Sie kostenlos zu Versicherung und Hausverwaltung.",
    introEn: "Lost keys are stressful but manageable. We open your door without damage, replace the cylinder if needed and advise you on insurance and building management – free of charge.",
    stepsDe: [
      "Ruhe bewahren – noch einmal alle Taschen prüfen.",
      "Letzte Orte rekonstruieren (Café, U-Bahn, Arbeit).",
      "Bei Hausschlüssel: Hausverwaltung informieren.",
      "Schlüsseldienst rufen – wir öffnen in 15–20 Min.",
      "Zylinder tauschen lassen – damit niemand Fremdes hineinkommt.",
    ],
    stepsEn: [
      "Stay calm – recheck every pocket and bag.",
      "Retrace the last locations (café, metro, work).",
      "House key: inform building management.",
      "Call a locksmith – we arrive in 15–20 min.",
      "Have the cylinder replaced – so no stranger can enter.",
    ],
    causesDe: ["Verlust unterwegs", "Diebstahl der Tasche", "Schlüssel im Schloss steckengelassen", "Kinder haben Schlüssel mitgenommen"],
    causesEn: ["Lost on the go", "Bag stolen", "Key left in the lock", "Children took the key"],
    relatedServiceSlug: "schlosswechsel-wien",
    priceFrom: 79,
    faqs: [
      { q: { de: "Zahlt die Versicherung den Schlosstausch?", en: "Does insurance pay for the lock change?" }, a: { de: "Viele Haushaltsversicherungen übernehmen die Kosten bei Schlüsselverlust. Bewahren Sie unbedingt die Rechnung auf.", en: "Many home insurance policies cover lock replacement after key loss. Always keep the invoice." } },
      { q: { de: "Muss ich wirklich den Zylinder tauschen?", en: "Do I really have to change the cylinder?" }, a: { de: "Wenn der Schlüssel mit Adresse verloren ging: ja, dringend. Sonst zumindest empfohlen.", en: "If the key was lost with your address attached: yes, urgently. Otherwise strongly recommended." } },
    ],
  },
  {
    slug: "tuer-zugefallen-wien",
    icon: "DoorClosed",
    titleDe: "Tür zugefallen in Wien – schnelle Hilfe",
    titleEn: "Door slammed shut in Vienna – fast help",
    shortDe: "Tür ist zugefallen, Schlüssel ist drinnen? Wir öffnen schadenfrei in 15 Minuten – Festpreis ab 79 €.",
    shortEn: "Door slammed shut, key inside? We open damage-free in 15 minutes – fixed price from €79.",
    keywordsDe: ["Tür zugefallen Wien", "ausgesperrt Wien", "zugefallene Tür öffnen"],
    introDe: "Eine zugefallene Tür ist der häufigste Notfall in Wien. In 95 % der Fälle öffnen wir Ihre Tür mit Pick-Technik schadenfrei – ohne Schloss aufbohren. Sie zahlen einen Festpreis ab 79 €, kein Stundensatz.",
    introEn: "A slammed door is the most common emergency in Vienna. In 95% of cases we open the door damage-free with picking tools – no drilling. You pay a fixed price from €79, no hourly rate.",
    stepsDe: [
      "Tief durchatmen – das passiert täglich in Wien.",
      "Nachbarn fragen – haben Sie einen Zweitschlüssel hinterlegt?",
      "Hausverwaltung kontaktieren (oft auch Notdienst).",
      "Schlüsseldienst rufen – wir kommen in 15 Min.",
      "Festpreis am Telefon bestätigen lassen.",
    ],
    stepsEn: [
      "Take a deep breath – it happens daily in Vienna.",
      "Ask neighbours – do you have a spare key with anyone?",
      "Contact building management (often has a service).",
      "Call a locksmith – we arrive in 15 min.",
      "Get the fixed price confirmed by phone.",
    ],
    causesDe: ["Zugluft / Wind", "Schloss auf Falle gestellt", "Kinder haben Tür zugezogen", "Eile beim Müll rausbringen"],
    causesEn: ["Draft / wind", "Latch only", "Children closed the door", "Rush to take out trash"],
    relatedServiceSlug: "tueroeffnung-wien",
    priceFrom: 79,
    faqs: [
      { q: { de: "Wie schnell sind Sie da?", en: "How fast are you here?" }, a: { de: "In den meisten Wiener Bezirken in 15–20 Minuten.", en: "In most Vienna districts within 15–20 minutes." } },
      { q: { de: "Wird die Tür dabei beschädigt?", en: "Will the door be damaged?" }, a: { de: "In 95 % der Fälle nicht – wir arbeiten mit professionellem Picking-Werkzeug.", en: "In 95% of cases no – we use professional picking tools." } },
    ],
  },
  {
    slug: "schloss-kaputt-wien",
    icon: "Lock",
    titleDe: "Schloss kaputt in Wien – Reparatur & Tausch",
    titleEn: "Broken lock in Vienna – repair & replacement",
    shortDe: "Schloss klemmt, dreht durch oder lässt sich nicht mehr öffnen? Wir reparieren oder tauschen sofort.",
    shortEn: "Lock jamming, spinning or not opening? We repair or replace immediately.",
    keywordsDe: ["Schloss kaputt Wien", "Schloss klemmt Wien", "Schloss reparieren"],
    introDe: "Ein defektes Schloss ist ein Sicherheitsrisiko. Wir analysieren vor Ort, ob Reparatur reicht oder ein neuer Marken-Zylinder (EVVA, ABUS, KABA) eingebaut werden muss.",
    introEn: "A defective lock is a security risk. On-site we determine whether a repair is enough or a new brand cylinder (EVVA, ABUS, KABA) is required.",
    stepsDe: [
      "Versuchen Sie nicht mit Gewalt zu drehen – Schaden wird teurer.",
      "Kein Öl oder WD-40 verwenden – verstopft das Schloss.",
      "Foto vom Schloss machen – hilft bei der Beratung am Telefon.",
      "Schlüsseldienst rufen – wir bringen Ersatzteile mit.",
    ],
    stepsEn: [
      "Do not force the key – damage gets more expensive.",
      "No oil or WD-40 – clogs the lock.",
      "Take a photo of the lock – helps for phone advice.",
      "Call a locksmith – we bring spare parts.",
    ],
    causesDe: ["Verschleiß durch Alter", "Eingedrungener Schmutz", "Verbogener Schlüssel", "Aufbruchversuch"],
    causesEn: ["Wear and age", "Dirt inside", "Bent key", "Burglary attempt"],
    relatedServiceSlug: "schlosswechsel-wien",
    priceFrom: 89,
    faqs: [
      { q: { de: "Reparatur oder Austausch?", en: "Repair or replace?" }, a: { de: "Bei Schlössern älter als 10 Jahre lohnt fast immer der Austausch – wegen Sicherheit und Verfügbarkeit von Ersatzteilen.", en: "For locks older than 10 years replacement is almost always worth it – security and parts availability." } },
    ],
  },
  {
    slug: "schluessel-abgebrochen-wien",
    icon: "KeySquare",
    titleDe: "Schlüssel abgebrochen im Schloss – Wien",
    titleEn: "Key broken in lock – Vienna",
    shortDe: "Schlüssel im Schloss abgebrochen? Wir entfernen das Reststück und tauschen den Zylinder bei Bedarf.",
    shortEn: "Key broken inside the lock? We remove the broken piece and replace the cylinder if needed.",
    keywordsDe: ["Schlüssel abgebrochen Wien", "Schlüssel im Schloss abgebrochen"],
    introDe: "Ein abgebrochener Schlüssel im Schloss ist heikel – falsches Hantieren beschädigt den Zylinder dauerhaft. Wir entfernen Reststücke mit Spezialwerkzeug schadenfrei.",
    introEn: "A broken key in the lock is delicate – wrong handling permanently damages the cylinder. We remove broken pieces with special tools without damage.",
    stepsDe: [
      "Schloss nicht drehen – Reststück nicht tiefer drücken.",
      "Kein Klebstoff verwenden (verstopft den Zylinder).",
      "Schlüsseldienst rufen – Spezialwerkzeug nötig.",
    ],
    stepsEn: [
      "Do not turn the lock – don't push the broken piece deeper.",
      "No glue – clogs the cylinder.",
      "Call a locksmith – special tools required.",
    ],
    causesDe: ["Alter, ermüdeter Schlüssel", "Krafteinwirkung beim Drehen", "Schlecht nachgemachter Schlüssel"],
    causesEn: ["Old, fatigued key", "Force while turning", "Poorly copied key"],
    relatedServiceSlug: "tueroeffnung-wien",
    priceFrom: 89,
    faqs: [
      { q: { de: "Muss ich den Zylinder tauschen?", en: "Do I have to replace the cylinder?" }, a: { de: "Nicht zwingend – wenn das Reststück sauber entfernt werden kann, funktioniert das Schloss meist weiter.", en: "Not necessarily – if the piece can be removed cleanly, the lock usually continues to work." } },
    ],
  },
  {
    slug: "wohnungstuer-oeffnen-wien",
    icon: "Home",
    titleDe: "Wohnungstür öffnen lassen in Wien",
    titleEn: "Apartment door opening in Vienna",
    shortDe: "Aus der Wohnung ausgesperrt? Wir öffnen schadenfrei – 24h, in jedem Wiener Bezirk.",
    shortEn: "Locked out of your apartment? We open damage-free – 24/7, in every Vienna district.",
    keywordsDe: ["Wohnungstür öffnen Wien", "Wohnung ausgesperrt", "Wohnungstür Notöffnung"],
    introDe: "Vor der eigenen Wohnungstür stehen und nicht reinkommen ist frustrierend. Unsere Techniker öffnen Wiener Wohnungstüren – auch im Altbau – meist in unter 5 Minuten und ohne Schaden.",
    introEn: "Standing in front of your own door without access is frustrating. Our technicians open Vienna apartment doors – including old buildings – usually in under 5 minutes without damage.",
    stepsDe: [
      "Identitätsausweis bereitlegen – wir prüfen, dass Sie Bewohner sind.",
      "Hausnummer und Stiege/Top am Telefon nennen.",
      "Schlüsseldienst kommt mit Festpreis – ab 79 €.",
    ],
    stepsEn: [
      "Have ID ready – we verify you live there.",
      "Tell us house number and staircase/top by phone.",
      "Locksmith arrives with fixed price – from €79.",
    ],
    causesDe: ["Tür zugefallen", "Schlüssel verloren", "Schloss defekt"],
    causesEn: ["Door slammed shut", "Key lost", "Lock defective"],
    relatedServiceSlug: "tueroeffnung-wien",
    priceFrom: 79,
    faqs: [
      { q: { de: "Brauche ich einen Identitätsnachweis?", en: "Do I need ID?" }, a: { de: "Ja – aus Sicherheitsgründen prüfen wir mit Ausweis, ob Sie der Bewohner sind.", en: "Yes – for safety reasons we check ID to confirm you live there." } },
    ],
  },
  {
    slug: "auto-aufgesperrt-wien",
    icon: "Car",
    titleDe: "Auto aufgesperrt – Schlüssel im PKW Wien",
    titleEn: "Car unlock – key inside vehicle Vienna",
    shortDe: "Schlüssel im Auto eingesperrt? Wir öffnen Ihren PKW schadenfrei – alle Marken.",
    shortEn: "Key locked inside the car? We open your vehicle damage-free – all brands.",
    keywordsDe: ["Auto aufgesperrt Wien", "Schlüssel im Auto eingesperrt", "PKW öffnen Wien"],
    introDe: "Schlüssel im Auto vergessen oder Funkbatterie leer? Wir kommen mobil zu Ihrem Standort in Wien und öffnen das Fahrzeug schadenfrei – auch mit moderner Wegfahrsperre.",
    introEn: "Forgot keys in the car or fob battery dead? We come mobile to your location in Vienna and open the vehicle damage-free – even with modern immobiliser.",
    stepsDe: [
      "Standort genau angeben (Bezirk, Straße, Hausnummer).",
      "Marke und Modell nennen.",
      "Fahrzeugschein bereithalten.",
    ],
    stepsEn: [
      "Give exact location (district, street, number).",
      "Tell us brand and model.",
      "Have vehicle registration ready.",
    ],
    causesDe: ["Schlüssel im Innenraum", "Funkbatterie leer", "Defektes Türschloss"],
    causesEn: ["Key inside cabin", "Fob battery dead", "Defective door lock"],
    relatedServiceSlug: "autooeffnung-wien",
    priceFrom: 89,
    faqs: [
      { q: { de: "Auch bei Funkschlüsseln?", en: "Also for keyless?" }, a: { de: "Ja, wir verfügen über die nötige Technik.", en: "Yes, we have the required technology." } },
    ],
  },
  {
    slug: "tresor-oeffnen-wien",
    icon: "Vault",
    titleDe: "Tresor öffnen lassen in Wien",
    titleEn: "Have your safe opened in Vienna",
    shortDe: "Code vergessen oder Schlüssel weg? Wir öffnen Tresore aller Marken diskret.",
    shortEn: "Forgot the code or lost the key? We open safes of all brands discreetly.",
    keywordsDe: ["Tresor öffnen Wien", "Safe knacken Wien", "Tresoröffnung Wien"],
    introDe: "Tresoröffnung erfordert Diskretion und Erfahrung. Wir öffnen Möbel-, Wand- und freistehende Tresore in Wien – auf Wunsch ohne sichtbare Beschädigung.",
    introEn: "Safe opening requires discretion and experience. We open furniture, wall and free-standing safes in Vienna – non-destructively on request.",
    stepsDe: [
      "Eigentumsnachweis bereithalten (Rechnung, Mietvertrag).",
      "Marke und Modell des Tresors nennen.",
      "Termin in Ruhe vereinbaren – Notdienst auf Anfrage.",
    ],
    stepsEn: [
      "Have proof of ownership ready (invoice, lease).",
      "Provide brand and model of the safe.",
      "Schedule an appointment – emergency on request.",
    ],
    causesDe: ["Code vergessen", "Schlüssel verloren", "Elektronik defekt"],
    causesEn: ["Forgotten code", "Lost key", "Defective electronics"],
    relatedServiceSlug: "tresoroeffnung-wien",
    priceFrom: 149,
    faqs: [
      { q: { de: "Bleibt der Inhalt unbeschädigt?", en: "Will the contents stay intact?" }, a: { de: "Ja, wir arbeiten gezielt am Schließsystem – nicht am Tresorkörper.", en: "Yes, we work on the locking mechanism, not the safe body." } },
    ],
  },
  {
    slug: "einbruch-tuer-sichern-wien",
    icon: "ShieldAlert",
    titleDe: "Nach Einbruch Tür sichern – Wien",
    titleEn: "Secure door after burglary – Vienna",
    shortDe: "Einbruch passiert? Wir sichern die Tür sofort und tauschen Schloss & Zylinder.",
    shortEn: "Burglary happened? We secure the door immediately and replace lock & cylinder.",
    keywordsDe: ["Einbruch Tür sichern Wien", "Nach Einbruch Schloss tauschen", "Notverriegelung Wien"],
    introDe: "Nach einem Einbruch zählt jede Stunde. Wir kommen 24/7, sichern die beschädigte Tür notdürftig, montieren neue Sicherheitsschlösser und beraten zur Versicherungsabwicklung.",
    introEn: "After a burglary every hour counts. We come 24/7, secure the damaged door, install new security locks and advise on insurance handling.",
    stepsDe: [
      "Polizei rufen (Notruf 133) – Anzeige für Versicherung.",
      "Nichts berühren bis zur Spurensicherung.",
      "Fotos für Versicherung machen.",
      "Schlüsseldienst – Sofortsicherung & neuer Zylinder.",
    ],
    stepsEn: [
      "Call police (emergency 133) – report for insurance.",
      "Don't touch anything until forensics arrive.",
      "Take photos for insurance.",
      "Locksmith – immediate securing & new cylinder.",
    ],
    causesDe: ["Aufgehebelte Tür", "Aufgebohrtes Schloss", "Kernziehung"],
    causesEn: ["Pried door", "Drilled lock", "Cylinder pulling"],
    relatedServiceSlug: "einbruchschutz-wien",
    priceFrom: 99,
    faqs: [
      { q: { de: "Übernimmt die Versicherung die Kosten?", en: "Does insurance cover the costs?" }, a: { de: "Bei polizeilich erfasstem Einbruch in der Regel ja – Rechnung aufbewahren.", en: "For burglaries reported to police usually yes – keep the invoice." } },
    ],
  },
  {
    slug: "briefkastenschloss-wien",
    icon: "Mailbox",
    titleDe: "Briefkastenschloss öffnen / tauschen Wien",
    titleEn: "Mailbox lock open / replace Vienna",
    shortDe: "Briefkasten ohne Schlüssel? Wir öffnen oder tauschen das Schloss schnell und günstig.",
    shortEn: "Mailbox without key? We open or replace the lock quickly and affordably.",
    keywordsDe: ["Briefkastenschloss Wien", "Briefkasten öffnen Wien", "Briefkasten Schlüssel verloren"],
    introDe: "Schlüssel zum Briefkasten verloren? Wir öffnen alle gängigen Briefkastenschlösser in Wien und tauschen sie gegen neue – günstig und ohne Beschädigung des Briefkastens.",
    introEn: "Lost the mailbox key? We open all common mailbox locks in Vienna and replace them – affordable and without damage.",
    stepsDe: [
      "Hausverwaltung informieren.",
      "Marke des Briefkastens fotografieren.",
      "Termin vereinbaren – meist am selben Tag möglich.",
    ],
    stepsEn: [
      "Inform building management.",
      "Photograph the mailbox brand.",
      "Schedule an appointment – usually same day.",
    ],
    causesDe: ["Schlüsselverlust", "Schloss verklemmt", "Vandalismus"],
    causesEn: ["Lost key", "Jammed lock", "Vandalism"],
    relatedServiceSlug: "schlosswechsel-wien",
    priceFrom: 39,
    faqs: [
      { q: { de: "Was kostet ein Briefkastenschloss?", en: "What does a mailbox lock cost?" }, a: { de: "Inklusive Montage ab ca. 39 € – abhängig vom Modell.", en: "Including installation from approx. €39 – depending on model." } },
    ],
  },
  {
    slug: "kellertuer-oeffnen-wien",
    icon: "PackageOpen",
    titleDe: "Kellertür / Garage öffnen lassen Wien",
    titleEn: "Cellar / garage door opening Vienna",
    shortDe: "Kellertür oder Garagentor zu? Wir öffnen schadenfrei in ganz Wien.",
    shortEn: "Cellar or garage door locked? We open damage-free across Vienna.",
    keywordsDe: ["Kellertür öffnen Wien", "Garage öffnen Wien", "Kellerschloss Wien"],
    introDe: "Auch Kellertüren, Abteile und Garagentore öffnen wir mobil in Wien – Vorhängeschlösser, Zylinderschlösser oder Profile-Türen.",
    introEn: "We also open cellar doors, storage units and garages mobile in Vienna – padlocks, cylinder locks or profile doors.",
    stepsDe: [
      "Hausverwaltung / Eigentümer informieren.",
      "Berechtigung nachweisen können.",
      "Schlüsseldienst rufen – mobile Anfahrt.",
    ],
    stepsEn: [
      "Inform building management / owner.",
      "Be able to prove authorisation.",
      "Call locksmith – mobile arrival.",
    ],
    causesDe: ["Verlorener Schlüssel", "Korrosion durch Feuchtigkeit", "Vandalismus"],
    causesEn: ["Lost key", "Corrosion from humidity", "Vandalism"],
    relatedServiceSlug: "tueroeffnung-wien",
    priceFrom: 69,
    faqs: [
      { q: { de: "Auch Vorhängeschlösser?", en: "Padlocks too?" }, a: { de: "Ja, alle gängigen Vorhängeschlösser.", en: "Yes, all common padlocks." } },
    ],
  },
  {
    slug: "schluessel-nachmachen-wien",
    icon: "Copy",
    titleDe: "Schlüssel nachmachen lassen in Wien",
    titleEn: "Have key copied in Vienna",
    shortDe: "Wohnungs-, Auto- oder Sicherheitsschlüssel nachmachen – schnell und präzise in Wien.",
    shortEn: "Apartment, car or security key copies – fast and precise in Vienna.",
    keywordsDe: ["Schlüssel nachmachen Wien", "Schlüsselkopie Wien", "Sicherheitsschlüssel kopieren"],
    introDe: "Wir kopieren Standard- und Sicherheitsschlüssel in Wien. Bei Sicherheits-Zylindern (EVVA, KABA) benötigen wir Ihre Sicherungskarte.",
    introEn: "We copy standard and security keys in Vienna. For security cylinders (EVVA, KABA) we need your security card.",
    stepsDe: [
      "Originalschlüssel mitbringen.",
      "Bei Sicherheitsschlüsseln: Sicherungskarte + Ausweis.",
      "Wartezeit oft nur 10–20 Minuten.",
    ],
    stepsEn: [
      "Bring original key.",
      "For security keys: security card + ID.",
      "Waiting time often only 10–20 minutes.",
    ],
    causesDe: ["Zweitschlüssel für Familie", "Vermietung", "Schlüsselverlust vermeiden"],
    causesEn: ["Spare for family", "Rental", "Avoid losing keys"],
    relatedServiceSlug: "schluesseldienst-wien",
    priceFrom: 9,
    faqs: [
      { q: { de: "Was kostet eine Kopie?", en: "What does a copy cost?" }, a: { de: "Standard ab 9 €, Sicherheitsschlüssel ab ca. 35 €.", en: "Standard from €9, security keys from approx. €35." } },
    ],
  },
  {
    slug: "schluessel-im-schloss-stecken-wien",
    icon: "AlertTriangle",
    titleDe: "Schlüssel steckt von innen – Tür öffnen Wien",
    titleEn: "Key stuck inside – door opening Vienna",
    shortDe: "Schlüssel steckt von innen, Tür ist zu? Wir öffnen mit Spezialtechnik schadenfrei.",
    shortEn: "Key stuck on the inside, door locked? We open with special technique – damage-free.",
    keywordsDe: ["Schlüssel steckt innen Wien", "Tür öffnen mit innen steckendem Schlüssel"],
    introDe: "Wenn der Zweitschlüssel von innen steckt, lässt sich die Tür von außen oft nicht mehr öffnen. Mit der sogenannten 'Schlüsselzieh-Technik' öffnen wir die Tür dennoch schadenfrei.",
    introEn: "If the second key is stuck on the inside, the door often cannot be opened from outside. With the 'key-pulling technique' we still open it without damage.",
    stepsDe: [
      "Nicht versuchen, mit eigenen Werkzeugen zu hantieren.",
      "Notfall-Telefonnummer wählen.",
      "Wir kommen sofort mit Spezialwerkzeug.",
    ],
    stepsEn: [
      "Don't use your own tools.",
      "Call the emergency number.",
      "We arrive immediately with special tools.",
    ],
    causesDe: ["Familienmitglied hat innen gesperrt", "Kinder", "Demenz"],
    causesEn: ["Family member locked from inside", "Children", "Dementia"],
    relatedServiceSlug: "tueroeffnung-wien",
    priceFrom: 89,
    faqs: [
      { q: { de: "Funktioniert das immer?", en: "Does it always work?" }, a: { de: "Bei den meisten Standardzylindern ja – bei Hochsicherheits-Zylindern muss eventuell der Zylinder getauscht werden.", en: "For most standard cylinders yes – for high-security cylinders the cylinder may need replacement." } },
    ],
  },
];

export const getProblem = (slug: string) => problems.find((p) => p.slug === slug);